import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { CalendarResourceRef, PwaCalendarBooking } from '@/calendar/pwaCalendarUtils';
import {
  formatMonthHeader,
  getBookingsForDay,
  getFreeResourcesForDay,
  getMonthGrid,
  isSameCalendarDay,
  navigateMonth,
  preferInternalBookings,
} from '@/calendar/pwaCalendarUtils';
import { BookingSourceIcon } from '@/components/BookingSourceIcon';
import { bookingSourceLabel } from '@/calendar/bookingSource';
import { airbnbStayKind, airbnbStayShortLabel, bookingOccupancyLabel } from '@/calendar/bookingListDisplay';

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'] as const;
const SLIDE_MS = 320;
const FALLBACK_RESOURCE_COLOR = '#3B82F6';

function resourcePillStyle(color: string | null | undefined): CSSProperties {
  const bg =
    color && /^#[0-9A-Fa-f]{6}$/.test(color) ? color : FALLBACK_RESOURCE_COLOR;
  const hex = bg.slice(1);
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return {
    backgroundColor: bg,
    color: luminance > 0.62 ? '#0f172a' : '#ffffff',
  };
}

function truncate(s: string, max: number): string {
  const t = s.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function formatShortTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
}

function statusLabel(status: string): string {
  if (status === 'confirmed') return 'Conf.';
  if (status === 'pending_deposit') return 'Pend.';
  if (status === 'human_supervision') return 'Humano';
  if (status.includes('cancel')) return 'Canc.';
  return status;
}

/** Zoom levels: 1 = normal pills, 2 = medium detail, 3 = full detail */
type ZoomLevel = 1 | 2 | 3;

type MonthPanelProps = {
  year: number;
  month: number;
  bookings: PwaCalendarBooking[];
  resources: CalendarResourceRef[];
  showFreeOnly: boolean;
  today: Date;
  selectedDay: Date | null;
  isCenter: boolean;
  zoomLevel: ZoomLevel;
  onOpenDay: (date: Date) => void;
};

function MonthPanel({
  year,
  month,
  bookings,
  resources,
  showFreeOnly,
  today,
  selectedDay,
  isCenter,
  zoomLevel,
  onOpenDay,
}: MonthPanelProps) {
  const weeks = useMemo(() => getMonthGrid(year, month), [year, month]);
  const maxPills = zoomLevel === 1 ? 3 : zoomLevel === 2 ? 5 : 8;

  return (
    <div className={`cal-month-panel${isCenter ? ' cal-month-panel--center' : ''}`}>
      <div className="cal-weekdays">
        {WEEKDAYS.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className={`cal-grid cal-grid--zoom-${zoomLevel}`}>
        {weeks.map((week, wi) =>
          week.map((cell) => {
            const dayBookings = isCenter
              ? preferInternalBookings(getBookingsForDay(bookings, cell.date))
              : [];
            const freeResources =
              isCenter && showFreeOnly ? getFreeResourcesForDay(resources, bookings, cell.date) : [];
            const isToday = isSameCalendarDay(cell.date, today);
            const isSelected = selectedDay ? isSameCalendarDay(cell.date, selectedDay) : false;
            const muted = !cell.isCurrentMonth;
            const cellItems = showFreeOnly ? freeResources : dayBookings;
            const visible = cellItems.slice(0, maxPills);
            const overflow = cellItems.length - maxPills;

            return (
              <button
                type="button"
                key={`${year}-${month}-${wi}-${cell.day}`}
                className={[
                  'cal-cell',
                  muted ? 'cal-cell--muted' : '',
                  isToday ? 'cal-cell--today' : '',
                  isSelected ? 'cal-cell--selected' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => onOpenDay(cell.date)}
              >
                <span className="cal-cell__num">{cell.day}</span>
                {showFreeOnly ?
                  (visible as CalendarResourceRef[]).map((r) => (
                    <span
                      key={r.id}
                      className="cal-pill cal-pill--free"
                      style={resourcePillStyle(r.color)}
                      title={`${r.name} — libre`}
                    >
                      {truncate(r.name, zoomLevel === 1 ? 9 : 14)}
                    </span>
                  ))
                : (visible as PwaCalendarBooking[]).map((b) => {
                    const stayKind = airbnbStayKind(b);
                    const stayLabel = airbnbStayShortLabel(b) ?? b.customerName;
                    const pillClass = [
                      'cal-pill',
                      'cal-pill--resource',
                      stayKind === 'reserved' ? 'cal-pill--airbnb-reserved' : '',
                      stayKind === 'block' ? 'cal-pill--airbnb-block' : '',
                    ]
                      .filter(Boolean)
                      .join(' ');
                    return (
                    <span
                      key={b.id}
                      className={pillClass}
                      style={resourcePillStyle(b.resourceColor)}
                      title={`${bookingOccupancyLabel(b)} — ${b.resourceName} — ${bookingSourceLabel(b)}${
                        b.alsoBlockedOnAirbnb ? ' — También bloqueado en Airbnb' : ''
                      }`}
                    >
                      {zoomLevel === 1 ? (
                        <>
                          <BookingSourceIcon
                            bookingOrigin={b.bookingOrigin}
                            otaChannel={b.otaChannel}
                            conversationChannel={b.conversationChannel}
                            size={12}
                          />
                          {truncate(b.resourceName, 9)}
                        </>
                      ) : zoomLevel === 2 ? (
                        <>
                          <BookingSourceIcon
                            bookingOrigin={b.bookingOrigin}
                            otaChannel={b.otaChannel}
                            conversationChannel={b.conversationChannel}
                            size={12}
                          />
                          {truncate(stayLabel, 12)} · {truncate(b.resourceName, 10)}
                        </>
                      ) : (
                        <span className="cal-pill__detail">
                          <BookingSourceIcon
                            bookingOrigin={b.bookingOrigin}
                            otaChannel={b.otaChannel}
                            conversationChannel={b.conversationChannel}
                            size={13}
                          />
                          {stayKind === 'block' ? (
                            <span className="cal-pill__name--block">{truncate(stayLabel, 18)}</span>
                          ) : (
                            <strong>{truncate(stayLabel, 18)}</strong>
                          )}
                          <span>
                            {b.resourceName} · {formatShortTime(b.startDate)} · {statusLabel(b.status)}
                          </span>
                        </span>
                      )}
                    </span>
                    );
                  })}
                {overflow > 0 ? <span className="cal-overflow">+{overflow} más</span> : null}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

type Props = {
  year: number;
  month: number;
  bookings: PwaCalendarBooking[];
  resources?: CalendarResourceRef[];
  showFreeOnly?: boolean;
  loadingMonth: boolean;
  loadErr: string | null;
  today: Date;
  selectedDay: Date | null;
  onMonthChange: (dir: 'prev' | 'next') => void;
  onOpenDay: (date: Date) => void;
};

export function CalendarMonthCarousel({
  year,
  month,
  bookings,
  resources = [],
  showFreeOnly = false,
  loadingMonth,
  loadErr,
  today,
  selectedDay,
  onMonthChange,
  onOpenDay,
}: Props) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [skipTransition, setSkipTransition] = useState(false);
  const touchRef = useRef<{ x: number; y: number; axis: 'none' | 'x' | 'y' } | null>(null);
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>(1);
  const pinchRef = useRef<{ startDist: number; startZoom: ZoomLevel } | null>(null);

  const prevYm = useMemo(() => navigateMonth(year, month, 'prev'), [year, month]);
  const nextYm = useMemo(() => navigateMonth(year, month, 'next'), [year, month]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const handler = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
      }
    };
    el.addEventListener('touchmove', handler, { passive: false });
    return () => el.removeEventListener('touchmove', handler);
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const measure = () => setViewportWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const resetToCenter = useCallback(() => {
    setSkipTransition(true);
    setDragOffset(0);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setSkipTransition(false));
    });
  }, []);

  const slideTo = useCallback(
    (dir: 'prev' | 'next') => {
      if (!viewportWidth || animating) return;
      setAnimating(true);
      setDragOffset(dir === 'next' ? -viewportWidth : viewportWidth);
      window.setTimeout(() => {
        onMonthChange(dir);
        resetToCenter();
        setAnimating(false);
      }, SLIDE_MS);
    },
    [viewportWidth, animating, onMonthChange, resetToCenter]
  );

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (animating) return;
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchRef.current = { startDist: Math.hypot(dx, dy), startZoom: zoomLevel };
        return;
      }
      const t = e.touches[0];
      if (!t) return;
      touchRef.current = { x: t.clientX, y: t.clientY, axis: 'none' };
      setDragging(false);
    },
    [animating, zoomLevel]
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const currentDist = Math.hypot(dx, dy);
        const ratio = currentDist / pinchRef.current.startDist;
        if (ratio > 1.25 && pinchRef.current.startZoom < 3) {
          setZoomLevel((pinchRef.current.startZoom + 1) as ZoomLevel);
          pinchRef.current = {
            startDist: currentDist,
            startZoom: (pinchRef.current.startZoom + 1) as ZoomLevel,
          };
        } else if (ratio < 0.75 && pinchRef.current.startZoom > 1) {
          setZoomLevel((pinchRef.current.startZoom - 1) as ZoomLevel);
          pinchRef.current = {
            startDist: currentDist,
            startZoom: (pinchRef.current.startZoom - 1) as ZoomLevel,
          };
        }
        return;
      }

      const start = touchRef.current;
      if (!start || animating || !viewportWidth) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;

      if (start.axis === 'none') {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          touchRef.current = { ...start, axis: 'y' };
          return;
        }
        touchRef.current = { ...start, axis: 'x' };
        setDragging(true);
      }

      if (start.axis === 'y' || touchRef.current?.axis === 'y') return;

      const maxDrag = viewportWidth * 0.92;
      const clamped = Math.max(-maxDrag, Math.min(maxDrag, dx));
      setDragOffset(clamped);
    },
    [animating, viewportWidth]
  );

  const snapBack = useCallback(() => {
    setAnimating(true);
    setDragOffset(0);
    window.setTimeout(() => setAnimating(false), SLIDE_MS);
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (pinchRef.current) {
        pinchRef.current = null;
        return;
      }

      const start = touchRef.current;
      touchRef.current = null;
      setDragging(false);

      if (!start || start.axis !== 'x' || !viewportWidth || animating) return;

      const t = e.changedTouches[0];
      if (!t) return;

      const dx = t.clientX - start.x;
      const threshold = viewportWidth * 0.18;

      if (dx < -threshold) slideTo('next');
      else if (dx > threshold) slideTo('prev');
      else snapBack();
    },
    [viewportWidth, slideTo, snapBack, animating]
  );

  const onTouchCancel = useCallback(() => {
    touchRef.current = null;
    setDragging(false);
    if (!animating) snapBack();
  }, [snapBack, animating]);

  const baseX = viewportWidth > 0 ? -viewportWidth : 0;
  const translateX = baseX + dragOffset;
  const useTransition = !dragging && !skipTransition;

  return (
    <div className="cal-carousel">
      <div className="cal-toolbar">
        <button
          type="button"
          className="cal-nav-btn"
          aria-label="Mes anterior"
          disabled={animating}
          onClick={() => slideTo('prev')}
        >
          ‹
        </button>
        <h2 className="cal-toolbar__month" aria-live="polite">
          {formatMonthHeader(year, month)}
        </h2>
        <button
          type="button"
          className="cal-nav-btn"
          aria-label="Mes siguiente"
          disabled={animating}
          onClick={() => slideTo('next')}
        >
          ›
        </button>
      </div>

      {loadErr ? <p className="pwa-err">{loadErr}</p> : null}
      {loadingMonth ? <p className="pwa-muted cal-swipe__loading">Actualizando…</p> : null}

      <div
        ref={viewportRef}
        className={`cal-month-viewport${dragging ? ' cal-month-viewport--dragging' : ''}`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
        aria-label="Calendario mensual. Deslizá horizontalmente para cambiar de mes."
      >
        <div
          className="cal-month-track"
          style={{
            transform: `translate3d(${translateX}px, 0, 0)`,
            transition: useTransition ? `transform ${SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)` : 'none',
          }}
        >
          <MonthPanel
            year={prevYm.year}
            month={prevYm.month}
            bookings={[]}
            resources={[]}
            showFreeOnly={false}
            today={today}
            selectedDay={null}
            isCenter={false}
            zoomLevel={zoomLevel}
            onOpenDay={onOpenDay}
          />
          <MonthPanel
            year={year}
            month={month}
            bookings={bookings}
            resources={resources}
            showFreeOnly={showFreeOnly}
            today={today}
            selectedDay={selectedDay}
            isCenter
            zoomLevel={zoomLevel}
            onOpenDay={onOpenDay}
          />
          <MonthPanel
            year={nextYm.year}
            month={nextYm.month}
            bookings={[]}
            resources={[]}
            showFreeOnly={false}
            today={today}
            selectedDay={null}
            isCenter={false}
            zoomLevel={zoomLevel}
            onOpenDay={onOpenDay}
          />
        </div>
      </div>
    </div>
  );
}
